import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const faqCategories = [
    {
        title: "Booking & Reservations",
        faqs: [
            {
                question: "How far in advance should I book transportation?",
                answer:
                    "We recommend booking as early as possible to ensure vehicle availability, especially during peak travel seasons and major events. However, we also accommodate last-minute transportation requests whenever possible.",
            },
            {
                question: "How do I request a quote?",
                answer:
                    "You can request a quote through our contact form, by phone, or by email. Simply provide your trip details, passenger count, travel dates, and destination information.",
            },
            {
                question: "Can I modify my reservation after booking?",
                answer:
                    "Yes. We understand travel plans can change. Our team will work with you to accommodate modifications whenever possible based on vehicle availability and scheduling.",
            },
            {
                question: "Do you require a deposit?",
                answer:
                    "Deposit requirements may vary depending on the type of trip and reservation details. Our team will provide all payment information during the booking process.",
            },
        ],
    },
    {
        title: "Vehicles & Fleet",
        faqs: [
            {
                question: "What types of vehicles do you offer?",
                answer:
                    "Our fleet includes executive sedans, luxury SUVs, sprinter vans, mini coaches, charter buses, and luxury motor coaches designed to accommodate groups of all sizes.",
            },
            {
                question: "How many passengers can your vehicles accommodate?",
                answer:
                    "Our vehicles accommodate anywhere from 1 passenger to large groups of 50+ passengers depending on the selected vehicle type.",
            },
            {
                question: "Are your vehicles regularly maintained?",
                answer:
                    "Absolutely. Every vehicle undergoes routine inspections, preventative maintenance, and safety checks to ensure reliability and passenger safety.",
            },
            {
                question: "Do your vehicles have luggage space?",
                answer:
                    "Yes. Our fleet is equipped with luggage storage options suitable for airport transportation, long-distance travel, and group trips.",
            },
        ],
    },
    {
        title: "Transportation Services",
        faqs: [
            {
                question: "Do you provide airport transportation?",
                answer:
                    "Yes. We offer professional airport transfer services for individuals, families, executives, and groups.",
            },
            {
                question: "Do you offer corporate transportation services?",
                answer:
                    "Yes. We provide executive transportation, employee shuttles, conference transportation, and customized corporate travel solutions.",
            },
            {
                question: "Can you accommodate weddings and special events?",
                answer:
                    "Absolutely. Our transportation services are ideal for weddings, concerts, sporting events, festivals, and private celebrations.",
            },
            {
                question: "Do you provide long-distance transportation?",
                answer:
                    "Yes. We offer both local and long-distance charter transportation services depending on your travel requirements.",
            },
        ],
    },
    {
        title: "Safety & Support",
        faqs: [
            {
                question: "Are your drivers professionally trained?",
                answer:
                    "Yes. Our drivers are experienced professionals committed to safety, customer service, and reliable transportation.",
            },
            {
                question: "Is customer support available after booking?",
                answer:
                    "Yes. Our team remains available to assist you before, during, and after your trip.",
            },
            {
                question: "What happens if my travel plans change?",
                answer:
                    "Our support team will work closely with you to accommodate schedule adjustments whenever possible.",
            },
            {
                question: "Is passenger safety your top priority?",
                answer:
                    "Absolutely. Safety is at the core of everything we do, from vehicle maintenance to driver training and trip planning.",
            },
        ],
    },
];

const Faq = () => {
    const [openIndex, setOpenIndex] = useState<string | null>("");

    const toggleFaq = (categoryIndex: number, faqIndex: number) => {
        const currentIndex = `${categoryIndex}-${faqIndex}`;

        setOpenIndex(
            openIndex === currentIndex ? null : currentIndex
        );
    };

    return (
        <section className="bg-primary">
            <Helmet>
                <title>FAQ | Velox Charter Transportation</title>

                <meta
                    name="description"
                    content="Find answers to frequently asked questions about Velox Charter's transportation services, including bookings, airport transfers, corporate travel, group transportation, fleet information, pricing, safety, and customer support."
                />
            </Helmet>

            <div className="max-w-7xl mx-auto px-4">
                <div className="h-[220px] md:h-[280px] flex items-center justify-center">
                    <div className="text-center">

                        <h1 className="text-white text-4xl md:text-6xl font-bold">
                            Frequently Asked Questions
                        </h1>

                        <p className="mt-5 text-white/80 max-w-2xl mx-auto text-base md:text-lg">
                            Find answers to common questions about our
                            transportation services, fleet, booking process,
                            and customer support.
                        </p>

                    </div>
                </div>
            </div>

            <section className="bg-[#F9FAFB] py-16 md:py-24">
                <div className="max-w-5xl mx-auto px-4">

                    <div className="text-center">
                        <h2 className="text-4xl md:text-5xl font-semibold text-black">
                            Everything You Need To Know
                        </h2>

                        <p className="mt-6 text-gray-500 leading-8 max-w-3xl mx-auto">
                            We've compiled answers to some of the most common
                            questions our clients ask. If you can't find the
                            information you're looking for, our team is always
                            available to help.
                        </p>
                    </div>

                </div>
            </section>

            <section className="bg-white py-16 lg:py-24">
                <div className="max-w-5xl mx-auto px-4">

                    {faqCategories.map((category, categoryIndex) => (
                        <div
                            key={categoryIndex}
                            className="mb-16 last:mb-0"
                        >

                            <div className="mb-8">
                                <h2 className="text-3xl md:text-4xl font-semibold text-black">
                                    {category.title}
                                </h2>
                            </div>

                            <div className="space-y-5">

                                {category.faqs.map((faq, faqIndex) => {
                                    const currentIndex = `${categoryIndex}-${faqIndex}`;
                                    const isOpen =
                                        openIndex === currentIndex;

                                    return (
                                        <div
                                            key={faqIndex}
                                            className="
                                                border
                                                border-[#E5E7EB]
                                                rounded-[20px]
                                                overflow-hidden
                                                bg-white
                                            "
                                        >

                                            <button
                                                onClick={() =>
                                                    toggleFaq(
                                                        categoryIndex,
                                                        faqIndex
                                                    )
                                                }
                                                className="
                                                    w-full
                                                    flex
                                                    justify-between
                                                    items-center
                                                    text-left
                                                    p-6
                                                    md:p-8
                                                "
                                            >
                                                <h3 className="font-semibold text-lg md:text-xl pr-6">
                                                    {faq.question}
                                                </h3>

                                                <span className="material-symbols-outlined text-secondryBlue">
                                                    {isOpen
                                                        ? "remove"
                                                        : "add"}
                                                </span>
                                            </button>

                                            {isOpen && (
                                                <div className="px-6 md:px-8 pb-8">
                                                    <p className="text-gray-500 leading-8">
                                                        {faq.answer}
                                                    </p>
                                                </div>
                                            )}

                                        </div>
                                    );
                                })}

                            </div>

                        </div>
                    ))}

                </div>
            </section>

            <section className="bg-[#F5F5F5] py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-4">

                    <div className="grid lg:grid-cols-2 gap-12 items-center">

                        <div>
                            <img
                                src="/images/faq-support.png"
                                alt="Customer Support"
                                className="
                                    w-full
                                    h-[320px]
                                    md:h-[450px]
                                    object-cover
                                    rounded-[30px]
                                "
                            />
                        </div>

                        <div>

                            <h2 className="text-[36px] md:text-[52px] font-semibold leading-tight">
                                Still Have Questions?
                            </h2>

                            <div className="mt-6 space-y-5 text-[#666666] leading-8 text-base md:text-lg">

                                <p>
                                    Our team is here to help. Whether you need
                                    assistance choosing the right vehicle,
                                    planning transportation logistics, or
                                    requesting a custom quote, we're ready to
                                    assist you.
                                </p>

                                <p>
                                    We pride ourselves on providing responsive,
                                    friendly, and professional customer support
                                    before, during, and after every trip.
                                </p>

                            </div>

                        </div>

                    </div>

                </div>
            </section>

            <section className="bg-primary py-20">
                <div className="max-w-5xl mx-auto px-4 text-center">

                    <h2 className="text-white text-4xl md:text-5xl font-semibold">
                        Ready To Plan Your Next Trip?
                    </h2>

                    <p className="mt-6 text-white/80 text-base md:text-lg max-w-3xl mx-auto">
                        Contact our team today and let us help create the
                        perfect transportation solution for your needs.
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
                        <Link
                            to="/contact-us"
                            className="
        inline-block
        bg-white
        text-primary
        px-8
        py-4
        rounded-full
        font-semibold
        hover:scale-105
        transition-all
    "
                        >
                            Contact Us
                        </Link>
                    </div>

                </div>
            </section>

        </section>
    );
};

export default Faq;